// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Field-name alignment for the ADR-004 D2 winner-only meta enrichment
 * (`FirmwareDeliveryAPIv1.GetFirmwarePackageMetaByReferenceId`, AU0008
 * ADR-014), mirroring how updater-au-response-mapper.ts and
 * au0001-response-mapper.ts already reconcile a raw foreign-AU
 * response_payload onto a widget contract shape.
 *
 * ProcessServiceRequest.ds relays FirmwareDeliveryAPIv1's raw response onto
 * response_payload with no renaming; by the time this mapper sees it,
 * rendezvous.ts's readFileObject has already unwrapped a non-null `data`
 * object one level (the same rule the AU0008/AU0004 and AU0001 mappers rely
 * on), so `raw` here IS that inner data object directly — or, when the
 * server-side branch had nothing to report (empty/absent capability
 * response), the surviving `{ success: false, error }` envelope.
 *
 * PROVISIONAL field names (release / draft_solution / operation_time /
 * operated_by / method) — AU0008 ADR-014 fixes the class-2 concept (bytes-free
 * release/version/method/solution-marker metadata) but not yet the literal
 * wire spelling reconciled against a real payload. Follows the same
 * "reconcile against a real payload once tested" path #22/#24 already walked
 * for AU0008/AU0004/AU0001 (ADR-004 D2, #23).
 */

import { toUnknownableString, UNKNOWN } from "@/contracts/display";
import type { EnrichMetaResponse } from "@/contracts/requests";

function toStringOrEmpty(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Map a raw FirmwareDeliveryAPIv1 meta response_payload onto EnrichMetaResponse.
 * Idempotent: a payload that already carries a `resolved` field (e.g. the mock
 * transport's contract-shaped fixtures) passes through unchanged.
 *
 * A missing/malformed meta record — the reference id did not resolve, or the
 * response shape does not carry the expected fields — maps to
 * `{ resolved: false }`, which the orchestrator turns into the ADR-004 D5
 * `meta_resolution_failed` halt (a reference id that SHOULD resolve but does
 * not is a reported halt, never a silent/partial display — ISO/IEC 5055).
 */
export function mapFirmwareDeliveryMetaResponse(raw: unknown): EnrichMetaResponse {
  if (typeof raw !== "object" || raw === null) {
    return { resolved: false };
  }
  const rec = raw as Record<string, unknown>;

  // Already-mapped pass-through (e.g. mock transport fixtures).
  if ("resolved" in rec) {
    return rec as unknown as EnrichMetaResponse;
  }

  // The surviving envelope on an empty/failed capability response
  // (ProcessServiceRequest.ds's `{success:false, error:...}` map) carries no
  // release/method fields — treat as unresolved rather than fabricating meta.
  if (rec["success"] === false || !("release" in rec) && !("method" in rec)) {
    return { resolved: false };
  }

  const method = toStringOrEmpty(rec["method"]);
  if (method.trim().length === 0) {
    return { resolved: false };
  }

  return {
    resolved: true,
    // `deploymentChannel` is intentionally left UNKNOWN here: FirmwareDeliveryAPIv1
    // has no notion of which updater AU delivered the package it produced —
    // that provenance is the D1 winner candidate's, and the orchestrator
    // overrides this field from there (ADR-004 D4).
    meta: {
      release: toUnknownableString(rec["release"]),
      draftSolution: toUnknownableString(rec["draft_solution"]),
      operationTime: toUnknownableString(rec["operation_time"]),
      operatedBy: toUnknownableString(rec["operated_by"]),
      method: toUnknownableString(rec["method"]),
      deploymentChannel: UNKNOWN,
    },
    updateMethod: method,
  };
}
